// @LTG-ID: data/journals/owner-dependence-business-value
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md, /app/docs/09_CHANGELOG.md#CHG-0107
// @CHANGED: 2026-07-06 (CHG-0107 · Journals content port from batch MD)
// @OWNER: web
//
// Article 8 of 20. Source: batch MD at /app/_ltg_analysis/content_batches/.
// Byline: LTG Team (per CHG-0107 orchestrator Decision 2 / batch preamble directive).

export const BRIEF = {
    SLUG: "owner-dependence-business-value",
    FRONTMATTER: {
        number: "45",
        section: "Value Drivers",
        sectionHref: "/journals#value-drivers",
        sectionAnchor: "value-drivers",
        title: "Owner Dependence: The Biggest Hidden Discount on Your Business's Value",
        deck: "Owner dependence means a business's revenue, operations, or customer relationships rely heavily on the owner personally.",
        author: "LTG Team",
        authorRole: "",
        authorInitials: "LT",
        publishedISO: "2026-05-13",
        publishedHuman: "May 13, 2026",
        lastReviewed: "2026-08-27",
        readMinutes: 3,
        wordCount: 450,
        timeRequired: "PT3M",
        keywords: "owner dependence business value, owner dependent business, how to make a business run without the owner, key person risk small business",
        articleSection: "Value Drivers",
        articleBodyPreview: "Owner dependence means a business's revenue, operations, or customer relationships rely heavily on the owner personally. Buyers discount owner dependent businesses because the value may not transfer after the sale. Reducing owner dependence, through delegation, documentation, and team building, is one of the highest impact ways to increase business value. Ask a business broker what kills more deals than anything else and you will hear the same answer in different words: the business is the owner",
        complianceNote: "This article is educational and does not constitute financial, legal, or tax advice.",
        needsDisclaimerFooter: false,
        internalLinks: [
        "how-much-is-my-business-worth",
        "exit-planning-guide",
        "what-is-a-valuation-multiple"
    ],
        ctaId: "journal-article-primary",
        ctaLabel: "Get Your Valuation",
        pageTitle: "Owner Dependence: The Biggest Hidden Discount on Value · LTG",
        canonical: "https://letstalkgrowth.com/journals/owner-dependence-business-value",
        ogImage: "/journals/og/owner-dependence-business-value.jpg",
    },
    BODY_MARKDOWN: `Owner dependence means a business's revenue, operations, or customer relationships rely heavily on the owner personally. Buyers discount owner-dependent businesses because the value may not transfer after the sale. Reducing owner dependence, through delegation, documentation, and team building, is one of the highest-impact ways to increase business value.

Ask a business broker what kills more deals than anything else and you will hear the same answer in different words: the business is the owner.

## The test buyers apply

Sophisticated buyers run a simple thought experiment: if the owner disappears the day after closing, what happens? If the honest answer is that sales stop, key customers waver, and nobody can price a job or run payroll, then the buyer is not purchasing a company. They are purchasing a job with an expensive entry fee, and they will price it accordingly, structure the deal defensively with earnouts and long transition periods, or walk away.

## Where dependence hides

Owner dependence is rarely one big thing. It accumulates in small ones:

- **Sales.** The owner personally originates most new business, and customers believe they are buying the owner.
- **Relationships.** The top customer accounts speak only to the owner.
- **Knowledge.** Pricing, scheduling, supplier terms, and the tricks of the trade live in the owner's head, not in documents.
- **Approvals.** Nothing above a trivial threshold happens without the owner's sign-off.
- **Licenses.** In trades and professional practices, required licenses or certifications sometimes sit with the owner personally, a structural form of dependence that needs early legal attention.

## The unwinding, in order

Reducing dependence is a sequence, not an event, and it typically takes one to three years to show results a buyer will believe.

First, document. Turn the contents of your head into checklists, price books, and process notes that someone competent could follow.

Second, delegate with authority. Handing off tasks while retaining every decision changes nothing in a buyer's eyes. The goal is decisions made well without you.

Third, transfer relationships. Introduce second contacts into every key customer and supplier relationship, and let your team run meetings while you fade to a supporting role.

Fourth, prove it. The strongest evidence is absence: a business that ran smoothly while the owner took a real vacation, multiple weeks, phone quiet, is telling buyers the truth in a way no marketing document can.

## The payoff shows up twice

Reduced owner dependence raises the price buyers will pay, and it also improves the terms: more cash at closing, shorter transition obligations, fewer contingencies. Owners who do this work also routinely report an unexpected side effect: the business becomes more profitable and more pleasant to own, which makes the decision to sell a genuine choice rather than an escape.`,
    FAQ: [
        { question: "How do buyers detect owner dependence?", answer: "Through diligence questions about who holds customer relationships, who approves decisions, what is documented, and what happened during the owner's absences. Organization charts, email patterns, and customer interviews reveal the truth quickly." },
        { question: "How long does it take to reduce owner dependence?", answer: "Meaningful change typically takes one to three years, because it involves hiring or developing people, documenting knowledge, and transferring relationships, then letting a track record accumulate." },
        { question: "Does owner dependence matter if my child is taking over the business?", answer: "Yes. A family successor inherits the same gap in systems, relationships, and documented knowledge, and any lender financing the transition will evaluate the business's ability to perform without the founder." }
    ],
    SOURCES: [

    ],
    END_CTA: {
        label: "READY TO KNOW YOUR NUMBER?",
        headlineLead: "What is your business ",
        headlineEm: "actually",
        headlineTrail: " worth?",
        body: "Indicative valuation signed by Sara. $995 flat.",
        secondaryHref: "/journals",
        secondaryLabel: "More from the Journal →",
        microLine: "· 7-DAY DELIVERY · KGOB METHODOLOGY, NC CPA #30420 ·",
    },
    READ_NEXT: [
        {
            tag: "Valuation",
            title: "How Much Is My Business Worth? The Owner's Complete Guide to Business Valuation",
            meta: "5 min · Brief 38",
            to: "/journals/how-much-is-my-business-worth",
        },
        {
            tag: "Exit Planning",
            title: "Exit Planning 101: How to Leave Your Business on Your Own Terms",
            meta: "4 min · Brief 39",
            to: "/journals/exit-planning-guide",
        },
        {
            tag: "Valuation",
            title: "What Is a Valuation Multiple and Why Does Everyone Talk About Them?",
            meta: "3 min · Brief 42",
            to: "/journals/what-is-a-valuation-multiple",
        },
    ],
};

export default BRIEF;
